/* eslint-disable */
// Lens Bank data access — React Query hooks over lens_bank / lens_link / the metrics RPCs, plus
// RPC wrappers. Real Units/Models/Best-sellers arrive from lens_metrics()/family_metrics() once
// the sales↔lens link is refreshed. Every read degrades gracefully if the table/RPCs aren't
// deployed yet (empty state / "—", no crash). Units only — no monetary metric.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { supabase } from '../../../supabase';

// Exported so the global ⌘K search can share this exact cache entry (Brief D Phase 3): same key +
// same queryFn ⇒ visiting the portal warms ⌘K and vice-versa. Do NOT fork the key.
export const LENS_BANK_KEY = ['lens-bank'];
export const lensBankQueryFn = async () => {
  const { data, error } = await supabase.from('lens_bank').select('*').is('deleted_at', null).order('lens_no');
  if (error) throw error;
  return data || [];
};

export function useLensBank() {
  const q = useQuery({ queryKey: LENS_BANK_KEY, queryFn: lensBankQueryFn, staleTime: 60000, retry: 0 });
  return { lenses: q.data || [], isLoading: q.isLoading, error: q.error };
}

export function useLensRefetch() {
  const qc = useQueryClient();
  // E4: also invalidate the Sales-side ['lens-display'] cache so a lens edit shows up in Sales
  // analytics WITHOUT a manual refresh (closes the SYS-0 P2 cross-portal staleness seam).
  return () => { qc.invalidateQueries({ queryKey: LENS_BANK_KEY }); qc.invalidateQueries({ queryKey: ['lens-display'] }); };
}

// E3 (SYS-1) — central capture for every lens WRITE RPC (all 7 go through here).
// Capture only genuine Postgres/network errors; the expected envelope conditions
// ('forbidden' RLS denial, 'reassignment_required' delete guard) are app-level UX,
// not system faults — skip them so Sentry stays signal.
const EXPECTED_ENVELOPE = new Set(['forbidden', 'reassignment_required']);
const rpc = async (name, args) => {
  const { data, error } = await supabase.rpc(name, args);
  if (error) {
    try { Sentry.captureException(error, { tags: { area: 'lens', op: name } }); } catch (_) {}
    throw error;
  }
  if (data && data.error) {
    if (!EXPECTED_ENVELOPE.has(data.error)) {
      try { Sentry.captureException(new Error(data.error), { tags: { area: 'lens', op: name } }); } catch (_) {}
    }
    throw new Error(data.error);
  }
  return data;
};

export const lensUpsert = (payload) => rpc('lens_upsert', { payload });
// Barcode governance (package A/D). allocate = compute-only next barcode for a family (used by the
// create-flow preview + the authoritative allocation at save). change = the ONLY sanctioned live
// barcode change (admin, reasoned, audited). auditList = recent changes for the Registry panel.
export const lensAllocateNo = (family) => rpc('lens_allocate_no', { p_family: family });
export const lensChangeBarcode = (id, newNo, reason) => rpc('lens_change_barcode', { p_id: id, p_new_lens_no: newNo, p_reason: reason });
export const lensAuditList = (limit = 50) => rpc('lens_no_audit_list', { p_limit: limit });
export const lensBulkUpsert = (rows) => rpc('lens_bulk_upsert', { rows });
export const lensSetYaeli = (lens_no, flag) => rpc('lens_set_yaeli', { p_lens_no: lens_no, p_flag: flag });
// Soft-delete a lens; with a target, its lens_link rows are repointed (inherited) to the target
// first. No target + >30 models → { error:'reassignment_required', models }. See Brief C.
export const lensDelete = (lens_no, target_lens_no = null) => rpc('lens_delete', { p_lens_no: lens_no, p_target_lens_no: target_lens_no });
export const lensRestore = (lens_no) => rpc('lens_restore', { p_lens_no: lens_no });
export const lensRefreshLink = () => rpc('refresh_lens_link', {});
export const lensLinkOverride = (lens_color, category, lens_no) =>
  rpc('lens_link_override', { p_lens_color: lens_color, p_category: category, p_lens_no: lens_no });
// E4 category backfill (the 1,881 project). set → refresh handled server-side.
export const setMasterCategory = (barcodes, category) => rpc('set_master_category', { p_barcodes: barcodes, p_category: category });
// E4-B curation bulk actions.
export const lensLinkOverrideBulk = (rows) => rpc('lens_link_override_bulk', { rows });
export const lensLinkIgnore = (lens_color, category) => rpc('lens_link_ignore', { p_lens_color: lens_color, p_category: category });

// Barcode migration (lens-lab packages A/B). by_name = bulk match-by-normalized-raw_name renumber
// (server is authoritative); by_id = single manual match onto a chosen target row; report = a
// progress snapshot for the migration screen. All three gate has_lens_admin() server-side.
export const lensMigrateByName = (rows) => rpc('lens_migrate_by_name', { rows });
export const lensMigrateById = (id, newLensNo, fields = {}) =>
  rpc('lens_migrate_by_id', { p_id: id, p_new_lens_no: newLensNo, p_fields: fields });
export const lensMigrationReport = () => rpc('lens_migration_report', {});

// The "missing category" work queue (get_null_category_queue). Empty/errors quietly (E2 pattern —
// pre-E4-SQL the RPC 404s → the panel shows an "awaiting E4 SQL" state, never crashes).
export function useNullCategoryQueue() {
  return useQuery({
    queryKey: ['null-category-queue'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_null_category_queue');
      if (error) throw error;
      return data || [];
    },
    staleTime: 60000, retry: 0,
  });
}

// Real Units/Models per lens + units per family (from the sales↔lens link). Returns Maps for
// O(1) lookup; empty Maps until the migration runs + refresh_lens_link() is called (cards → "—").
// Optional { from, to } (ISO dates) scopes every figure to that sales period (lens-period-v1).
// No range ⇒ p_from/p_to omitted ⇒ RPCs behave byte-identically to before (all-time).
const isMissingFnErr = (e) => e && (e.code === 'PGRST202' || /PGRST202|Could not find the function/i.test(e.message || ''));
export function useLensMetrics(range) {
  const from = range?.from || null;
  const to = range?.to || null;
  const hasRange = !!(from && to);
  const q = useQuery({
    queryKey: ['lens-metrics', from, to],
    queryFn: async () => {
      const args = hasRange ? { p_from: from, p_to: to } : undefined;
      // Conv #153 client half: if the period-aware signature isn't deployed yet the params-call
      // 404s (PGRST202) → retry WITHOUT params so the UI degrades to all-time, never errors.
      const call = async (name) => {
        let res = await supabase.rpc(name, args);
        if (res.error && hasRange && isMissingFnErr(res.error)) res = await supabase.rpc(name);
        return res;
      };
      const [m, f] = await Promise.all([call('lens_metrics'), call('family_metrics')]);
      if (m.error) throw m.error;
      if (f.error) throw f.error;
      const byLens = new Map();
      (m.data || []).forEach((r) => byLens.set(r.lens_no, { units: Number(r.units) || 0, models: Number(r.models) || 0 }));
      const byFamily = new Map();
      (f.data || []).forEach((r) => byFamily.set(r.family, { units: Number(r.units) || 0, lenses: Number(r.lenses) || 0 }));
      return { byLens, byFamily };
    },
    staleTime: 60000, retry: 0,
  });
  return { byLens: q.data?.byLens || new Map(), byFamily: q.data?.byFamily || new Map(), isLoading: q.isLoading };
}

// lens_link rows (read = authenticated) — drives the curation screen.
export function useLensLink() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['lens-link'],
    queryFn: async () => {
      const { data, error } = await supabase.from('lens_link').select('*').order('units_hint', { ascending: false, nullsFirst: false });
      if (error) throw error;
      return data || [];
    },
    staleTime: 60000, retry: 0,
  });
  return { rows: q.data || [], isLoading: q.isLoading, error: q.error, refetch: () => { qc.invalidateQueries({ queryKey: ['lens-link'] }); qc.invalidateQueries({ queryKey: ['lens-display'] }); } };
}

export function useMetricsRefetch() {
  const qc = useQueryClient();
  return () => { qc.invalidateQueries({ queryKey: ['lens-metrics'] }); qc.invalidateQueries({ queryKey: ['lens-link'] }); qc.invalidateQueries({ queryKey: ['lens-display'] }); };
}
